import { FormEvent, ReactNode, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { IconArrowRight, IconCheck } from '../components/icons';
import { Logo, OceanBackdrop } from '../components/ocean';
import { MfaStep } from '../components/MfaStep';
import { Field } from '../components/ui';
import { api, ApiError, InviteInfo } from '../lib/api';
import { useSession } from '../lib/session';
import { t } from '../lib/i18n';

const ROLE_LABEL: Record<string, string> = { owner: 'владелец', admin: 'администратор', developer: 'разработчик', viewer: 'наблюдатель' };

function errorText(e: unknown): string {
  if (!(e instanceof ApiError)) return t('Сеть недоступна. Попробуйте ещё раз.');
  if (e.code === 'INVALID_TOKEN') return t('Ссылка недействительна или устарела. Запросите новую.');
  if (e.code === 'INVALID_CREDENTIALS') return t('Неверный пароль.');
  if (e.code === 'RATE_LIMITED') return t('Слишком много попыток. Подождите минуту.');
  if (e.code === 'VALIDATION_FAILED') return e.details?.[0]?.message ?? t('Проверьте поля формы.');
  return e.message;
}

function Shell({ title, lead, children }: { title: string; lead?: ReactNode; children: ReactNode }) {
  return (
    <div className="ocean-screen">
      <OceanBackdrop letters={10} />
      <div style={{ position: 'relative', zIndex: 2, display: 'grid', placeItems: 'center', minHeight: '100vh', padding: 20 }}>
        <div className="glass auth-card stack" style={{ width: 'min(440px, 100%)' }}>
          <div className="row">
            <Logo size={36} />
            <strong style={{ color: '#fff', fontSize: 18 }}>MailOcean</strong>
          </div>
          <div>
            <h2>{title}</h2>
            {lead && <p className="muted" style={{ marginTop: 6 }}>{lead}</p>}
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api.post('/auth/password-reset/request', { email });
      setSent(true);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell title={t('Потеряли якорь?')} lead={t('Пришлём ссылку для нового пароля. Она действует 60 минут и срабатывает один раз.')}>
      {sent ? (
        <>
          <div className="callout ok"><IconCheck /> {t('Если аккаунт с адресом {email} существует, письмо уже в пути.', { email })}</div>
          <Link to="/login" className="btn btn-primary btn-lg">{t('Вернуться ко входу')}</Link>
        </>
      ) : (
        <form className="stack" onSubmit={submit}>
          <Field label="Email">
            <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required autoFocus />
          </Field>
          {error && <div className="callout bad">{error}</div>}
          <button className="btn btn-primary btn-lg" disabled={busy || !email}>
            {busy ? t('Отправляем…') : t('Прислать ссылку')} <IconArrowRight size={16} />
          </button>
          <p className="muted small" style={{ textAlign: 'center' }}><Link to="/login">{t('Я вспомнил пароль')}</Link></p>
        </form>
      )}
    </Shell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { refresh } = useSession();
  const nav = useNavigate();
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const done = async () => (await refresh(), nav('/', { replace: true }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (password !== repeat) return setError(t('Пароли не совпадают'));
    setBusy(true);
    setError('');
    try {
      const res = await api.post<{ mfa_required?: boolean; mfa_token?: string }>('/auth/password-reset/confirm', { token, password });
      if (res.mfa_required && res.mfa_token) return setMfaToken(res.mfa_token);
      await done();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell title={t('Новый пароль')} lead={t('После смены пароля все остальные сессии будут завершены.')}>
      {mfaToken ? (
        <>
          <div className="callout ok">{t('Пароль изменён. Подтвердите вход вторым фактором.')}</div>
          <MfaStep token={mfaToken} onDone={done} />
        </>
      ) : !token ? (
        <div className="callout bad">{t('В ссылке нет токена.')} <Link to="/forgot-password">{t('Запросить новую ссылку')}</Link></div>
      ) : (
        <form className="stack" onSubmit={submit}>
          <Field label={t('Новый пароль')} hint={t('Минимум 10 символов')}>
            <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" required autoFocus />
          </Field>
          <Field label={t('Повторите пароль')}>
            <input className="input" type="password" value={repeat} onChange={(e) => setRepeat(e.target.value)} autoComplete="new-password" required />
          </Field>
          {error && <div className="callout bad">{error}</div>}
          <button className="btn btn-primary btn-lg" disabled={busy || password.length < 10}>
            {busy ? t('Сохраняем…') : t('Сохранить и войти')} <IconArrowRight size={16} />
          </button>
        </form>
      )}
    </Shell>
  );
}

export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { refresh } = useSession();
  const nav = useNavigate();
  const [info, setInfo] = useState<InviteInfo | null>(null);
  const [loadError, setLoadError] = useState('');
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return setLoadError(t('В ссылке нет токена приглашения.'));
    api.get<InviteInfo>(`/auth/invites/${encodeURIComponent(token)}`).then(setInfo).catch((e) => setLoadError(errorText(e)));
  }, [token]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await api.post<{ mfa_required?: boolean; mfa_token?: string }>(`/auth/invites/${encodeURIComponent(token)}/accept`, info?.user_exists ? { password } : { name, password });
      if (res.mfa_required && res.mfa_token) return setMfaToken(res.mfa_token);
      await refresh();
      nav('/', { replace: true });
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (loadError) {
    return (
      <Shell title={t('Приглашение не найдено')}>
        <div className="callout bad">{loadError}</div>
        <Link to="/login" className="btn btn-primary btn-lg">{t('Ко входу')}</Link>
      </Shell>
    );
  }
  if (!info) return <Shell title={t('Проверяем приглашение…')}><div className="skeleton" style={{ height: 120 }} /></Shell>;

  return (
    <Shell
      title={t('Добро пожаловать в «{ws}»', { ws: info.workspace })}
      lead={<>{t('{who} приглашает', { who: info.invited_by ?? t('Команда') })} <b>{info.email}</b> {t('с ролью «{role}».', { role: t(ROLE_LABEL[info.role] ?? info.role) })}</>}
    >
      {mfaToken ? (
        <MfaStep token={mfaToken} onDone={async () => (await refresh(), nav('/', { replace: true }))} />
      ) : (
      <form className="stack" onSubmit={submit}>
        {info.user_exists ? (
          <div className="callout">{t('У вас уже есть аккаунт MailOcean — подтвердите паролем.')}</div>
        ) : (
          <Field label={t('Ваше имя')}>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" required autoFocus />
          </Field>
        )}
        <Field label={info.user_exists ? t('Пароль') : t('Придумайте пароль')} hint={info.user_exists ? undefined : t('Минимум 10 символов')}>
          <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete={info.user_exists ? 'current-password' : 'new-password'} required />
        </Field>
        {error && <div className="callout bad">{error}</div>}
        <button className="btn btn-primary btn-lg" disabled={busy || !password || (!info.user_exists && !name)}>
          {busy ? t('Ныряем…') : t('Присоединиться')} <IconArrowRight size={16} />
        </button>
        {info.user_exists && <p className="muted small" style={{ textAlign: 'center' }}><Link to="/forgot-password">{t('Забыли пароль?')}</Link></p>}
      </form>
      )}
    </Shell>
  );
}
